import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { differenceInDays, format } from 'date-fns'
import {
  ArrowRightIcon,
  CalendarIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

import { useCurrentWorkspace, useCombinedCalendar } from '../../store/hooks'
import { getRelativeTimeString } from '../../lib/utils/date.utils'
import { WorkspaceType } from '../../lib/db/db.types'

export function Dashboard() {
  const { myWorkspaces, onSetCurrentWorkspace } = useCurrentWorkspace()
  const { events } = useCombinedCalendar()

  const upcomingEvents = useMemo(() => {
    return events
      .filter((event) => {
        if (event.isCompleted) return false

        const eventDate = new Date(event.date)
        const today = new Date()
        const days = differenceInDays(eventDate, today)
        return days <= 30
      })
      .sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime()
      })
  }, [events])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen"
    >
      <div className="space-y-12">
        <div className="bg-gray-100 p-6 rounded-lg">
          {upcomingEvents.length > 0 ? (
            <>
              <h2 className="text-xl font-medium mb-4">
                {upcomingEvents.length}{' '}
                {upcomingEvents.length === 1 ? 'event' : 'events'} in the next
                30 days
              </h2>
              <div className="flex flex-col space-y-4">
                {upcomingEvents.map((event) => {
                  const eventDate = new Date(event.date)
                  const workspace = myWorkspaces.find(
                    (workspace) => workspace.id === event.workspaceId,
                  )
                  const relativeTime = getRelativeTimeString(eventDate)

                  if (!workspace) return null

                  return (
                    <div
                      key={`${event.workspaceId}-${event.id}-${event.date}`}
                      className="group relative bg-white hover:bg-gray-50 transition-colors shadow rounded-lg"
                    >
                      <Link
                        to={`/workspace/${event.workspaceId}/calendar/event/${event.id}`}
                        className="absolute inset-0 z-10"
                        aria-label={`View details for ${event.name}`}
                      />

                      <div className="p-4 space-y-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {workspace.name ?? '-'}
                        </span>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <div className="text-2xl font-bold">
                              {format(event.date, 'dd MMM')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {relativeTime}
                            </div>
                          </div>
                          <div>
                            <div className="text-xl font-medium text-gray-900">
                              {event.name}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <CalendarIcon className="h-10 w-10 text-gray-500" />
              <p className="text-xl">No events in next 30 days</p>
            </div>
          )}

          <div className="flex justify-center mt-4">
            <Link
              to="/calendar"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-500 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-300"
            >
              View all events
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div>
          <div className="flex flex-row justify-between items-center mb-4">
            <h2 className="text-xl font-medium">Your businesses</h2>
          </div>
          <div className="flex flex-col space-y-4">
            {myWorkspaces.map((b) => {
              if (b.spaceType !== WorkspaceType.BUSINESS) return null
              return (
                <div
                  key={b.id}
                  className="group relative hover:bg-blue-100 transition-colors rounded-lg border border-blue-200"
                >
                  <button
                    onClick={() => onSetCurrentWorkspace(b.id)}
                    className="absolute inset-0 z-10"
                  />
                  <div className="p-4 space-y-2">
                    <div className="text-base font-medium text-gray-900">
                      {b.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {b.business.business.companyNumber}
                    </div>
                  </div>
                </div>
              )
            })}
            <Link
              to="/new-business"
              className="hover:bg-blue-100 transition-colors rounded-lg border border-blue-200 p-4"
            >
              <div className="flex flex-col items-center">
                <span className="text-sm font-medium text-blue-500">
                  Add new business
                </span>
                <PlusIcon className="h-6 w-6 text-blue-500" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
